import React, { useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authApi, TenantBranding } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/sgokit.svg';
import { Arrow } from '../../platform/icons';

// Inline marks — sgokit doesn't carry an icon library the way grant_management
// does (heroicons); the auth *mechanism* is what we're porting, not the icons.
const CheckMark = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width={20} height={20} aria-hidden>
    <path
      fillRule="evenodd"
      d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 111.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z"
      clipRule="evenodd"
    />
  </svg>
);

// SGOKit-branded pitch aside, shared by the platform sign-in and its sent state.
// Mirrors SignUp's aside so the two front doors read as one product.
const PlatformAside = () => (
  <aside className="sgk-auth__aside">
    <Link to="/" className="sgk-brand" data-testid="login-brand-link">
      <img className="sgk-brand__logo" src={logo} alt="" />
      <span className="sgk-brand__name">SGO<b>Kit</b></span>
    </Link>
    <div className="sgk-auth__pitch">
      <h2>Your toolbox for <em>education empowerment.</em></h2>
      <p>Sign in to manage donations, your Stripe connection, and your public
        state footprint — the whole kit, one console.</p>
    </div>
    <p className="sgk-hero__note" style={{ position: 'relative' }}>
      Section 25F · federal credit up to <b>$1,700</b> · begins 2027
    </p>
  </aside>
);

// ── Shared magic-link send hook ──────────────────────────────────────────────

function useMagicLinkSend(nextPath: string) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authApi.sendMagicLink(email, nextPath);
      setSent(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return { email, setEmail, error, loading, sent, submit };
}

function SentPanel({ email }: { email: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto h-14 w-14 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
          <CheckMark />
        </div>
        <h2 className="text-2xl font-bold text-gray-900" data-testid="login-check-email-heading">
          Check your email
        </h2>
        <p className="text-gray-600" data-testid="login-check-email-message">
          We sent a sign-in link to <strong>{email}</strong>. It expires shortly.
        </p>
      </div>
    </div>
  );
}

// ── Platform (SGOKit admin) login — shown on sgokit.test ────────────────────

const PlatformLoginForm: React.FC<{ nextPath: string }> = ({ nextPath }) => {
  const { email, setEmail, error, loading, sent, submit } = useMagicLinkSend(nextPath);

  return (
    <div className="sgk-site">
      <div className="sgk-auth">
        <PlatformAside />
        <div className="sgk-auth__main">
          {sent ? (
            <div className="sgk-form" style={{ textAlign: 'center' }}>
              <div
                className="sgk-module__icon"
                style={{ width: 56, height: 56, borderRadius: 14, margin: '0 auto 20px' }}
              >
                <CheckMark />
              </div>
              <h1 data-testid="login-check-email-heading">Check your email</h1>
              <p className="sgk-form__sub" data-testid="login-check-email-message">
                We sent a sign-in link to <strong>{email}</strong>. It expires shortly.
              </p>
            </div>
          ) : (
            <form className="sgk-form" onSubmit={submit} data-testid="login-form">
              <h1>Sign in to SGOKit</h1>
              <p className="sgk-form__sub">Enter your email and we&apos;ll send you a sign-in link.</p>

              {error && (
                <div className="sgk-alert sgk-alert--error" data-testid="login-error-message">
                  {error}
                </div>
              )}

              <div className="sgk-field">
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="sgk-input"
                  placeholder="you@acme.org"
                  data-testid="login-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="sgk-btn sgk-btn--gold"
                data-testid="login-submit-button"
              >
                {loading ? 'Sending…' : <>Send sign-in link <Arrow /></>}
              </button>

              <Link to="/signup" className="sgk-authback" data-testid="login-signup-link">
                Need an organization? Start an SGO
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Tenant (org-branded) login — shown on <subdomain>.sgokit.test ───────────

const TenantLoginForm: React.FC<{ nextPath: string; branding: TenantBranding }> = ({
  nextPath,
  branding,
}) => {
  const { email, setEmail, error, loading, sent, submit } = useMagicLinkSend(nextPath);
  if (sent) return <SentPanel email={email} />;

  const brand = {
    primary: branding.primary_color,
    background: branding.background_color,
    text: branding.text_color,
    border: branding.border_color,
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: brand.background }}>
      <div className="px-6 py-4 flex items-center gap-3" style={{ backgroundColor: brand.primary }}>
        {branding.logo && (
          <img
            src={branding.logo}
            alt={branding.name}
            style={{ height: branding.logo_size, width: 'auto' }}
            className="object-contain flex-shrink-0"
          />
        )}
        {branding.name && <span className="text-white font-semibold text-sm">{branding.name}</span>}
      </div>
      <div className="flex-1 flex items-start justify-center pt-12 px-4">
        <div
          className="w-full max-w-md bg-white rounded-lg shadow-sm p-8"
          style={{ border: `1px solid ${brand.border}` }}
        >
          <h2 className="text-xl font-semibold mb-6 text-center" style={{ color: brand.text }}>
            Sign in to continue
          </h2>
          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 text-sm text-red-800" data-testid="tenant-login-error-message">
              {error}
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid="tenant-login-form">
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ borderColor: brand.border, color: brand.text }}
              className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2"
              placeholder="you@example.com"
              data-testid="tenant-login-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 text-sm font-medium rounded-md text-white disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: brand.primary }}
              data-testid="tenant-login-submit-button"
            >
              {loading ? 'Sending…' : 'Send sign-in link'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Top-level router — platform vs. tenant is server-driven, never host-parsed ─

const LoginForm: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const nextPath =
    searchParams.get('next') || (location.state as any)?.from?.pathname || '';

  const { data: branding } = useQuery<TenantBranding>({
    queryKey: ['tenant_branding'],
    queryFn: authApi.getBranding,
    staleTime: 10 * 60 * 1000,
  });

  if (user && nextPath) {
    return <Navigate to={nextPath} replace />;
  }
  if (!branding) return null; // avoid a flash of the wrong login form

  const forceAdmin = searchParams.get('admin') === '1';
  if (forceAdmin || !branding.name) {
    return <PlatformLoginForm nextPath={nextPath} />;
  }
  return <TenantLoginForm nextPath={nextPath} branding={branding} />;
};

export default LoginForm;
